/**
 * Die Anlagen des Accounts: auflisten, anlegen, archivieren.
 *
 * **Ein Bereich des Kontos, nicht der Anlage** — er steht deshalb in keiner der
 * beiden Listen der Seitenleiste: Welche Anlagen es gibt, ist keine Eigenschaft
 * einer einzelnen von ihnen. Erreicht wird er über den **Umschalter** am Kopf
 * der Leiste („Anlage hinzufügen"), und solange es gar keine Anlage gibt, über
 * den Knopf, der dort an seiner Stelle steht. Die Einstellungen *einer* Anlage
 * stehen in `routes/settings.tsx`.
 *
 * Seine Adresse ist `/konto/anlagen`. Archivierte Anlagen bleiben sichtbar und
 * werden nur gekennzeichnet:
 * Sie verschwinden zu lassen, würde die Frage „wo ist sie hin?" erzeugen, und
 * gelöscht wird ohnehin nichts.
 *
 * **Archivieren steht hier *und* in den Einstellungen der Anlage.** Das ist
 * keine Dopplung aus Versehen: Aus der Liste heraus archiviert man eine
 * *andere* Anlage, um im Kontingent Platz zu schaffen — ohne erst dorthin zu
 * wechseln. In den Einstellungen archiviert man die, die man gerade ansieht.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, Input } from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";
import { useState, type FormEvent } from "react";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { listRow, mutedText, plainList, rowLabel } from "../lib/list-styles.js";
import { useSites } from "../lib/queries.js";
import type { CurrentUser } from "../lib/session.js";

export interface SitesCardProps {
  readonly user: CurrentUser;
  /**
   * Wird nach dem Anlegen gerufen — **bevor** die Liste neu geladen ist. Der
   * Rahmen entscheidet damit, ob er die Ansicht wechselt: Nach der *ersten*
   * Anlage will man sie sehen und nicht auf der Liste stehen bleiben, aus der
   * für ein Standardkonto ohnehin kein Weg zurückführt. Bei der dritten Anlage
   * eines erweiterten Kontos wäre derselbe Sprung eine Bevormundung.
   *
   * **Die angelegte Anlage geht mit.** Sie steht noch in keiner Liste, und die
   * Adresse, zu der gesprungen wird, trägt ihre Kennung — der Rahmen könnte sie
   * sonst erst nachschlagen, wenn die Liste eingetroffen ist.
   */
  readonly onCreated?: (site: ApiComponents["schemas"]["Site"]) => void;
}

export function SitesCard({ user, onCreated }: SitesCardProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("Europe/Berlin");
  const [error, setError] = useState<string | null>(null);

  const sites = useSites();

  const create = useMutation({
    mutationFn: async () => {
      /**
       * `kind` wird mitgeschickt, obwohl der Vertrag dafür einen Vorgabewert hat:
       * Ein Zod-`.default()` erscheint im erzeugten OpenAPI als **Pflichtfeld**,
       * weil dort die Ausgabeform beschrieben wird, in der der Wert immer steht.
       * Der Client muss ihn deshalb nennen. Eine Auswahl der Anlagenart gehört in
       * die Oberfläche, sobald es mehr als ein Formularfeld gibt (HM-010).
       */
      const { data, error: failed } = await api.POST("/sites", {
        body: { name, timezone, kind: "single_family_house" },
      });
      // Die Meldung kommt vom Server — etwa bei unbekannter Zeitzone.
      if (failed || !data) throw new Error(apiErrorMessage(failed) ?? "Anlegen fehlgeschlagen.");
      return data;
    },
    onSuccess: async (site) => {
      setName("");
      setError(null);
      onCreated?.(site);
      await queryClient.invalidateQueries({ queryKey: ["sites"] });
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const archive = useMutation({
    mutationFn: async (siteId: string) => {
      const { error: failed } = await api.DELETE("/sites/{siteId}", {
        params: { path: { siteId } },
      });
      if (failed) throw new Error(apiErrorMessage(failed) ?? "Archivieren fehlgeschlagen.");
    },
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["sites"] }),
    onError: (cause: Error) => setError(cause.message),
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    create.mutate();
  }

  /**
   * Das Kontingent der Plattform-Rolle, hier nur zur **Anzeige**. Die verbindliche
   * Prüfung bleibt der Server — eine Oberfläche, die eine Regel selbst entscheidet,
   * ist genau so lange richtig, bis jemand die API direkt anspricht. Ohne diesen
   * Hinweis füllte ein Standard-Nutzer aber ein Formular aus, um danach ein `403`
   * zu lesen.
   */
  const activeSites = sites.data?.items.filter((site) => site.status === "active").length ?? 0;
  const quotaReached = user.siteQuota !== null && activeSites >= user.siteQuota;

  return (
    <Card
      title="Anlagen"
      subtitle={quotaLabel(user.siteQuota, activeSites)}
      /**
       * Der Akzentstreifen trägt die Spartenfarbe Strom — Wegweisung, keine
       * Datenaussage. Eine Größenfarbe hätte hier nichts zu suchen (ADR 0013).
       */
      accent="var(--hm-commodity-electricity)"
    >
      {sites.isPending && <p style={mutedText}>Wird geladen …</p>}

      {sites.data?.items.length === 0 && (
        <p style={mutedText}>Noch keine Anlage. Lege unten die erste an.</p>
      )}

      {sites.data && sites.data.items.length > 0 && (
        <ul style={plainList}>
          {sites.data.items.map((site) => (
            <li key={site.id} style={listRow}>
              <span style={rowLabel}>{site.name}</span>
              <span style={mutedText}>{site.timezone}</span>
              <span style={{ marginLeft: "auto" }}>
                {site.status === "archived" ? (
                  <Badge>archiviert</Badge>
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => archive.mutate(site.id)}>
                    Archivieren
                  </Button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {quotaReached ? (
        <p style={{ ...mutedText, marginTop: "var(--sp-4)" }}>
          Dein Konto führt {user.siteQuota === 1 ? "eine Anlage" : `${user.siteQuota} Anlagen`}, und
          damit ist es ausgeschöpft. Archiviere eine bestehende Anlage oder lass dein
          Konto erweitern.
        </p>
      ) : (
        <form
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: "var(--sp-3)",
            marginTop: "var(--sp-4)",
            flexWrap: "wrap",
          }}
          onSubmit={submit}
        >
          <Input
            label="Name der Anlage"
            value={name}
            placeholder="Haus am Hang"
            required
            style={{ flex: "1 1 180px" }}
            onChange={(event) => setName(event.target.value)}
          />
          <Input
            label="Zeitzone"
            value={timezone}
            placeholder="Europe/Berlin"
            required
            style={{ flex: "1 1 180px" }}
            onChange={(event) => setTimezone(event.target.value)}
          />
          <Button type="submit" disabled={create.isPending}>
            Anlegen
          </Button>
        </form>
      )}

      {error !== null && (
        <div style={{ marginTop: "var(--sp-3)" }}>
          <Alert>{error}</Alert>
        </div>
      )}
    </Card>
  );
}

/** „1 von 1 aktiv" — die Zahl, die erklärt, warum das Formular gleich verschwindet. */
function quotaLabel(quota: number | null, active: number): string {
  if (quota === null) return `${active} aktiv · unbegrenzt`;
  return `${active} von ${quota} aktiv`;
}
